import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';

const BACKEND_URL = (() => {
  const raw =
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_BACKEND_URL_DEV ||
    'http://localhost:4200';
  return raw.replace(/\/api\/v1\/?$/, '');
})();

/**
 * Hook that connects to the Standby WebSocket and joins a user room.
 * When the backend emits user:dashboard-updated, invalidates all
 * dashboard-related queries. Includes 15s polling fallback.
 */
export function useDashboardSocket(userId: number | undefined) {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['my-teams'] });
    queryClient.invalidateQueries({ queryKey: ['team-active-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['my-participating-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['expiring-reviews'] });
  };

  useEffect(() => {
    if (!userId) return;

    const socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-user', userId);
    });

    socket.on('user:dashboard-updated', () => {
      invalidateAll();
    });

    socket.on('reconnect', () => {
      socket.emit('join-user', userId);
      invalidateAll();
    });

    // Safety-net polling in case socket is silently dead
    const fallbackInterval = setInterval(() => {
      invalidateAll();
    }, 15000);

    return () => {
      clearInterval(fallbackInterval);
      socket.emit('leave-user', userId);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId, queryClient]);

  return socketRef;
}
